import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Lock, Search, Download, LogOut, Users, FileText, Mail, Phone, Calendar, RefreshCw } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const STORAGE_KEY = 'ewen_admin_token';

function toCSV(rows, cols) {
  const header = cols.map((c) => `"${c.label}"`).join(',');
  const body = rows.map((r) => cols.map((c) => `"${String(r[c.key] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  return `${header}\n${body}`;
}

function download(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function Login({ onOk }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(STORAGE_KEY, data.token);
      onOk(data.token);
    } catch (err) {
      toast({ title: 'Access denied', description: err?.response?.data?.detail || 'Wrong password.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#0f1a14] px-6 pt-24">
      <form onSubmit={submit} className="w-full max-w-md bg-[#152720] border border-white/10 p-10">
        <div className="h-12 w-12 grid place-items-center bg-[#c9a56a] text-[#0f1a14] mb-6">
          <Lock className="h-5 w-5" />
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#c9a56a]">Ewen Admin</div>
        <h1 className="font-serif text-3xl text-white mt-2">Sign in to continue</h1>
        <p className="text-white/60 text-sm mt-2">Enter the admin password to view leads and brochure requests.</p>
        <Input type="password" placeholder="Admin password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-6 h-12 bg-[#0f1a14] border-white/15 text-white rounded-none focus-visible:ring-[#c9a56a]" />
        <Button type="submit" disabled={busy} className="w-full mt-4 bg-[#c9a56a] text-[#0f1a14] hover:bg-[#b8945a] rounded-none py-6 tracking-wider">
          {busy ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>
    </div>
  );
}

function StatCard({ Icon, label, value, hint }) {
  return (
    <div className="bg-[#152720] border border-white/10 p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.25em] text-[#c9a56a]">{label}</div>
        <Icon className="h-5 w-5 text-[#c9a56a]" />
      </div>
      <div className="font-serif text-5xl text-white mt-4">{value}</div>
      {hint && <div className="text-xs text-white/50 mt-1">{hint}</div>}
    </div>
  );
}

function DataTable({ rows, cols, empty }) {
  if (!rows.length) return <div className="text-center text-white/50 py-16 border border-white/10">{empty}</div>;
  return (
    <div className="overflow-x-auto border border-white/10">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#152720] text-[#c9a56a] uppercase tracking-widest text-[10px]">
            {cols.map((c) => (
              <th key={c.key} className="text-left px-4 py-3 font-medium border-b border-white/10">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
              {cols.map((c) => (
                <td key={c.key} className="px-4 py-3 text-white/80 align-top">
                  {c.render ? c.render(r) : String(r[c.key] ?? '\u2014')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [interest, setInterest] = useState('all');
  const [project, setProject] = useState('all');
  const [range, setRange] = useState('all');

  const authHeaders = useMemo(() => ({ headers: { 'X-Admin-Password': token } }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const [l, b, a] = await Promise.all([
        axios.get(`${API}/leads`, authHeaders),
        axios.get(`${API}/brochure-requests`, authHeaders),
        axios.get(`${API}/career-applications`, authHeaders),
      ]);
      setLeads(l.data);
      setBrochures(b.data);
      setApplications(a.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast({ title: 'Session expired', description: 'Please sign in again.' });
        onLogout();
      } else {
        toast({ title: 'Failed to load', description: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const withinRange = (iso) => {
    if (range === 'all') return true;
    const d = new Date(iso);
    const now = new Date();
    const days = { '24h': 1, '7d': 7, '30d': 30 }[range];
    return (now - d) / 86400000 <= days;
  };

  const filteredLeads = leads.filter((l) => {
    const s = q.trim().toLowerCase();
    const matchesQ = !s || [l.name, l.email, l.phone, l.message, l.interest, l.source].some((v) => v && v.toLowerCase().includes(s));
    const matchesInterest = interest === 'all' || (l.interest || '').toLowerCase() === interest;
    return matchesQ && matchesInterest && withinRange(l.created_at);
  });

  const filteredBrochures = brochures.filter((b) => {
    const s = q.trim().toLowerCase();
    const matchesQ = !s || [b.name, b.email, b.phone, b.project_id].some((v) => v && v.toLowerCase().includes(s));
    const matchesProject = project === 'all' || (b.project_id || '') === project;
    return matchesQ && matchesProject && withinRange(b.created_at);
  });

  const filteredApplications = applications.filter((a) => {
    const s = q.trim().toLowerCase();
    const matchesQ = !s || [a.username, a.email, a.phone, a.previous_job, a.city, a.country].some((v) => v && v.toLowerCase().includes(s));
    return matchesQ && withinRange(a.created_at);
  });

  const leadCols = [
    { key: 'created_at', label: 'Date', render: (r) => new Date(r.created_at).toLocaleString() },
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'interest', label: 'Interest' },
    { key: 'source', label: 'Source' },
    { key: 'message', label: 'Message', render: (r) => <span className="line-clamp-2 max-w-xs block">{r.message || '\u2014'}</span> },
  ];
  const brochureCols = [
    { key: 'created_at', label: 'Date', render: (r) => new Date(r.created_at).toLocaleString() },
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'project_id', label: 'Project' },
  ];  
  
  const applicationCols = [
    { key: 'created_at', label: 'Date', render: (r) => new Date(r.created_at).toLocaleString() },
    { key: 'username', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'previous_job', label: 'Previous Role' },
    { key: 'city', label: 'City' },
    { key: 'country', label: 'Country' },
    { key: 'resume', label: 'Resume', render: (r) => r.resume ? <a href={r.resume} target="_blank" rel="noreferrer" className="text-[#c9a56a] underline break-all">Open</a> : '\u2014' },
  ];

  const exportLeads = () => download(`ewen-leads-${Date.now()}.csv`, toCSV(filteredLeads, leadCols.filter((c) => c.key !== 'message').concat([{ key: 'message', label: 'Message' }])));
  const exportBrochures = () => download(`ewen-brochures-${Date.now()}.csv`, toCSV(filteredBrochures, brochureCols));
  const exportApplications = () => download(`ewen-applications-${Date.now()}.csv`, toCSV(filteredApplications, applicationCols.filter((c) => c.key !== 'resume').concat([{ key: 'resume', label: 'Resume' }])));
  
  const projectIds = Array.from(new Set(brochures.map((b) => b.project_id).filter(Boolean)));
  const interests = Array.from(new Set(leads.map((l) => l.interest).filter(Boolean)));

  const newestLead = leads[0]?.created_at ? new Date(leads[0].created_at).toLocaleDateString() : '\u2014';

  return (
    <div className="min-h-screen bg-[#0f1a14] pt-24 pb-16">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#c9a56a]">Ewen Admin</div>
            <h1 className="font-serif text-4xl text-white mt-1">Enquiries Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <Button onClick={load} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white hover:text-[#0f1a14] rounded-none">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button onClick={onLogout} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white hover:text-[#0f1a14] rounded-none">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mt-10">
          <StatCard Icon={Users} label="Total Leads" value={leads.length} hint={`Latest: ${newestLead}`} />
          <StatCard Icon={FileText} label="Brochure Requests" value={brochures.length} />
          <StatCard Icon={Mail} label="Career Applications" value={applications.length} />
          <StatCard Icon={Phone} label="With Phone" value={leads.length} />
        </div>

        {/* Filters */}
        <div className="mt-10 grid md:grid-cols-4 gap-3">
          <div className="flex items-center gap-2 bg-[#152720] border border-white/10 px-3">
            <Search className="h-4 w-4 text-[#c9a56a]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={'Search name, phone, email\u2026'} className="bg-transparent border-0 text-white placeholder:text-white/40 focus-visible:ring-0 shadow-none h-12" />
          </div>
          <Select value={interest} onValueChange={setInterest}>
            <SelectTrigger className="rounded-none bg-[#152720] border-white/10 text-white h-12"><SelectValue placeholder="Interest" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All interests</SelectItem>
              {interests.map((i) => <SelectItem key={i} value={i.toLowerCase()}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={project} onValueChange={setProject}>
            <SelectTrigger className="rounded-none bg-[#152720] border-white/10 text-white h-12"><SelectValue placeholder="Project" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projectIds.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="rounded-none bg-[#152720] border-white/10 text-white h-12"><SelectValue placeholder="Date range" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-10">
          <Tabs defaultValue="leads">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <TabsList className="bg-[#152720] border border-white/10 rounded-none p-0">
                <TabsTrigger value="leads" className="rounded-none data-[state=active]:bg-[#c9a56a] data-[state=active]:text-[#0f1a14] text-white/70 px-6 py-3">Leads ({filteredLeads.length})</TabsTrigger>
                <TabsTrigger value="brochures" className="rounded-none data-[state=active]:bg-[#c9a56a] data-[state=active]:text-[#0f1a14] text-white/70 px-6 py-3">Brochures ({filteredBrochures.length})</TabsTrigger>
                <TabsTrigger value="applications" className="rounded-none data-[state=active]:bg-[#c9a56a] data-[state=active]:text-[#0f1a14] text-white/70 px-6 py-3">Applications ({filteredApplications.length})</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="leads" className="mt-4">
              <div className="flex justify-end mb-3">
                <Button onClick={exportLeads} className="bg-[#c9a56a] text-[#0f1a14] hover:bg-[#b8945a] rounded-none">
                  <Download className="h-4 w-4 mr-2" /> Export CSV
                </Button>
              </div>
              <DataTable rows={filteredLeads} cols={leadCols} empty="No leads match your filters." />
            </TabsContent>
            <TabsContent value="brochures" className="mt-4">
              <div className="flex justify-end mb-3">
                <Button onClick={exportBrochures} className="bg-[#c9a56a] text-[#0f1a14] hover:bg-[#b8945a] rounded-none">
                  <Download className="h-4 w-4 mr-2" /> Export CSV
                </Button>
              </div>
              <DataTable rows={filteredBrochures} cols={brochureCols} empty="No brochure requests match your filters." />
            </TabsContent>
            <TabsContent value="applications" className="mt-4">
              <div className="flex justify-end mb-3">
                <Button onClick={exportApplications} className="bg-[#c9a56a] text-[#0f1a14] hover:bg-[#b8945a] rounded-none">
                  <Download className="h-4 w-4 mr-2" /> Export CSV
                </Button>
              </div>
              <DataTable rows={filteredApplications} cols={applicationCols} empty="No career applications match your filters." />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || '');

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken('');
  };

  if (!token) return <Login onOk={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
